import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ConfigurationArchive,
  ConfigurationRestoreSupport,
  RestoreResult,
  deleteConfigurationArchive,
  downloadConfigurationArchive,
  getConfigurationRestoreSupport,
  listConfigurationArchives,
  restoreConfigurationArchive,
} from "api/ConfigurationArchiveHelpers";
import useConfirm from "hooks/useConfirm";
import { reportError } from "utils/diagnostics";
import { FullBackupNote } from "./full-backup-note.component";

interface Props {
  open: boolean;
  onClose: () => void;
  targetSerialNumbers: string[];
  initialSourceSerialNumber?: string;
}

function errorMessage(error: any) {
  return error?.response?.data?.error || error?.message || "Unable to access configuration archives.";
}

export function ConfigurationArchivesDialog({
  open,
  onClose,
  targetSerialNumbers,
  initialSourceSerialNumber = targetSerialNumbers[0] || "",
}: Props) {
  const [source, setSource] = useState("");
  const [loadedSource, setLoadedSource] = useState("");
  const [archives, setArchives] = useState<ConfigurationArchive[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<RestoreResult>();
  const [restoreSupport, setRestoreSupport] = useState<ConfigurationRestoreSupport & { targets: string }>();
  const restoreTargetKey = targetSerialNumbers.join(",");
  const currentRestoreSupport = restoreSupport?.targets === restoreTargetKey ? restoreSupport : undefined;
  const restoreReady = currentRestoreSupport?.supported === true;
  const generation = useRef(0);
  const [confirm, Confirmation] = useConfirm();

  useEffect(() => {
    setRestoreSupport(undefined);
    if (!open) return undefined;
    setResult(undefined);
    let active = true;
    const targets = restoreTargetKey ? restoreTargetKey.split(",") : [];
    if (!targets.length || targets.length > 100) {
      setRestoreSupport({
        targets: restoreTargetKey,
        supported: false,
        reason: "Select 1–100 MegaFi 2 devices to check restore availability.",
      });
    } else {
      getConfigurationRestoreSupport(targets)
        .then((support) => {
          if (active) setRestoreSupport({ ...support, targets: restoreTargetKey });
        })
        .catch((failure) => {
          if (!active) return;
          reportError("configuration.archives.restoreSupport", failure);
          setRestoreSupport({
            targets: restoreTargetKey,
            supported: false,
            reason: "Unable to check restore availability. Close and reopen this dialog to retry.",
          });
        });
    }
    return () => {
      active = false;
    };
  }, [open, restoreTargetKey]);

  const load = async (serial: string, nextCursor?: string) => {
    const current = ++generation.current;
    setError("");
    setLoading(true);
    if (!nextCursor) {
      setArchives([]);
      setSelected("");
      setCursor(undefined);
      setLoadedSource("");
    }
    try {
      const page = await listConfigurationArchives(serial, nextCursor);
      if (current !== generation.current) return;
      setArchives((previous) => (nextCursor ? [...previous, ...page.archives] : page.archives));
      setLoadedSource(serial);
      setCursor(page.nextCursor);
    } catch (failure) {
      if (current !== generation.current) return;
      reportError("configuration.archives.list", failure);
      setError(errorMessage(failure));
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSource(initialSourceSerialNumber);
      setResult(undefined);
      if (initialSourceSerialNumber) load(initialSourceSerialNumber);
    }
    return () => {
      generation.current += 1;
    };
  }, [open, initialSourceSerialNumber]);

  const download = async (archive: ConfigurationArchive) => {
    setBusy(true);
    setError("");
    try {
      const url = await downloadConfigurationArchive(loadedSource, archive.archiveId);
      const link = document.createElement("a");
      link.href = url;
      link.download = `config-${loadedSource}-${archive.archiveId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (failure) {
      reportError("configuration.archives.download", failure);
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (archive: ConfigurationArchive) => {
    const accepted = await confirm({
      title: "Delete Configuration Archive",
      content: "Delete this backup? Pending restores that use it will no longer be able to download it.",
      buttonTxt: "Delete",
    });
    if (!accepted) return;
    setBusy(true);
    setError("");
    try {
      await deleteConfigurationArchive(loadedSource, archive.archiveId);
      await load(loadedSource);
    } catch (failure) {
      reportError("configuration.archives.delete", failure);
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!restoreReady) return;
    setBusy(true);
    setError("");
    setResult(undefined);
    try {
      setResult(await restoreConfigurationArchive(loadedSource, selected, targetSerialNumbers, "polling"));
    } catch (failure) {
      reportError("configuration.archives.restore", failure);
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const locked = busy || loading;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Configuration Archives</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Backups already uploaded to MegaPortal are listed automatically. Download or delete a backup, or select one to
          restore to your selected devices. Remote restore requires compatible device firmware.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 2 }}>
          <TextField
            label="Backup source device serial number"
            helperText="Enter another device serial number to view its saved backups."
            value={source}
            onChange={(event) => {
              setSource(event.target.value);
              setSelected("");
              setResult(undefined);
            }}
            disabled={locked}
            fullWidth
          />
          <Button disabled={locked || !/^[0-9]{1,20}$/.test(source)} onClick={() => load(source)}>
            {source === loadedSource ? "Refresh list" : "Show backups"}
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <CircularProgress size={24} aria-label="Loading archives" />}
        {loadedSource && source === loadedSource && (
          <>
            <TableContainer>
              <Table size="small" aria-label="Configuration archives">
                <TableHead>
                  <TableRow>
                    <TableCell>Select</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archives.map((archive) => (
                    <TableRow key={archive.archiveId} selected={selected === archive.archiveId}>
                      <TableCell>
                        <Radio
                          checked={selected === archive.archiveId}
                          onChange={() => {
                            setSelected(archive.archiveId);
                            setResult(undefined);
                          }}
                          disabled={locked}
                          inputProps={{
                            "aria-label": `Select backup uploaded ${new Date(archive.uploadedAt).toLocaleString()}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>{new Date(archive.uploadedAt).toLocaleString()}</TableCell>
                      <TableCell>{Math.max(1, Math.ceil(archive.sizeBytes / 1024))} KB</TableCell>
                      <TableCell>
                        <Button disabled={locked} onClick={() => download(archive)}>
                          Download
                        </Button>
                        <Button disabled={locked} color="error" onClick={() => remove(archive)}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!archives.length && (
                    <TableRow>
                      <TableCell colSpan={4}>No backups have been uploaded for this device.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {cursor && (
              <Button disabled={locked} onClick={() => load(loadedSource, cursor)}>
                Load more backups
              </Button>
            )}
          </>
        )}
        <Typography sx={{ mt: 3, mb: 1 }}>
          Restore the selected backup to {targetSerialNumbers.length === 1 ? "device" : "devices"}:{" "}
          {targetSerialNumbers.join(", ")}
        </Typography>
        {restoreReady ? (
          <FullBackupNote />
        ) : (
          <Alert severity="info">
            {currentRestoreSupport?.reason || "Checking restore availability…"}
            {currentRestoreSupport && (
              <div>
                Remote full-backup restore needs compatible device firmware and cloud enablement. Selecting a backup
                does not enable it. Download and Delete remain available.
              </div>
            )}
          </Alert>
        )}
        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography>Command delivery results (these do not confirm that restoration finished):</Typography>
            {result.results.map((item) => (
              <Alert key={item.serialNumber} severity={item.error ? "error" : "success"} sx={{ mt: 1 }}>
                {item.serialNumber}:{" "}
                {item.error || (item.delivery === "mqtt" ? "Sent through MQTT." : "Queued for cloud polling.")}
              </Alert>
            ))}
          </Box>
        )}
        <Confirmation />
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={
            locked ||
            !restoreReady ||
            !selected ||
            source !== loadedSource ||
            !targetSerialNumbers.length ||
            targetSerialNumbers.length > 100 ||
            !!result
          }
          onClick={restore}
        >
          {busy
            ? "Working…"
            : `Restore on ${targetSerialNumbers.length} ${targetSerialNumbers.length === 1 ? "device" : "devices"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
